import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import { tasksApi, projectsApi, Task, TaskStatus, Project } from '@shared/api-client'

const statusColors: Record<TaskStatus, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  todo: 'default',
  in_progress: 'primary',
  in_review: 'warning',
  done: 'success',
  blocked: 'error',
  cancelled: 'error',
}

export default function TaskDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [task, setTask] = useState<Task | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      loadTask()
    }
  }, [id])

  const loadTask = async () => {
    try {
      const data = await tasksApi.get(Number(id))
      setTask(data)
      
      // Load project info
      if (data.project_id) {
        try {
          const projectData = await projectsApi.get(data.project_id)
          setProject(projectData)
        } catch (error) {
          console.error('Failed to load project:', error)
        }
      }
    } catch (error) {
      console.error('Failed to load task:', error)
      alert('Failed to load task')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString()
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  if (!task) {
    return <Typography>Task not found</Typography>
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tasks')}
        >
          Back to Tasks
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/tasks/${id}/edit`)}
        >
          Edit Task
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">{task.title}</Typography>
          <Chip
            label={task.status}
            color={statusColors[task.status]}
          />
        </Box>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
          {task.description || 'No description'}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Task Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography><strong>Priority:</strong> {task.priority}</Typography>
                <Typography><strong>Status:</strong> {task.status}</Typography>
                <Typography><strong>Project:</strong> {project?.name || task.project_id}</Typography>
                {task.milestone_id && (
                  <Typography><strong>Milestone ID:</strong> {task.milestone_id}</Typography>
                )}
                <Typography><strong>Due Date:</strong> {formatDate(task.due_date)}</Typography>
                <Typography><strong>Completed Date:</strong> {formatDate(task.completed_date)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Time Tracking
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography><strong>Estimated Hours:</strong> {task.estimated_hours || '-'}</Typography>
                <Typography><strong>Actual Hours:</strong> {task.actual_hours || '-'}</Typography>
                <Typography><strong>Created:</strong> {formatDate(task.created_at)}</Typography>
                <Typography><strong>Updated:</strong> {formatDate(task.updated_at)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  )
}

